import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import { useProjectedCrossesInfinite, useDeleteProjectedCross } from '../contexts/projected-crosses';
import ProjectedCrossForm from '../forms/projected-cross-form';

import '../../../common/styles/catalog.css';

// Catálogo de cruces proyecto (admin-projected-crosses.md): plantilla donadora × toro
// con código único y cupo. Se venden N veces como "vacas proyecto" (reserva en ventas).
const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

export default function ProjectedCrossesOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';
    const urlStatus = params.get('status') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);
    const [form, setForm] = useState(null); // null | 'new' | cross(edit)

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };
    const clearFilters = () => { setSearch(''); setParams(new URLSearchParams(), { replace: true }); };

    const { mutateAsync: remove, isPending: removing } = useDeleteProjectedCross();
    const confirm = useConfirm();

    const filters = useMemo(() => {
        const out = { sort: 'code' };
        if (debounced) out.q = debounced;
        if (urlStatus) out.status = urlStatus;
        return out;
    }, [debounced, urlStatus]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useProjectedCrossesInfinite(filters);
    const items = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? items.length;
    const hasFilters = !!(search || urlStatus);

    const del = async (c) => {
        const ok = await confirm({
            title: 'Eliminar cruce',
            message: `¿Eliminar el cruce "${c.code}"? (soft-delete; el par se puede reusar con otro código)`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(c.id); toast.success('Cruce eliminado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <div className="cat-overview">
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Cruces proyecto</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="projected_crosses" action="write">
                    <button type="button" className="cat-overview__add" onClick={() => setForm('new')}>
                        <IconPlus /> <span>Nuevo cruce</span>
                    </button>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por código o nombre…" aria-label="Buscar cruces por código o nombre" />
                </div>
                <select className="cat-overview__filter" value={urlStatus} onChange={(e) => setParam('status', e.target.value)} aria-label="Filtrar por estado">
                    <option value="">Activos e inactivos</option>
                    <option value="2">Activo</option>
                    <option value="1">Inactivo</option>
                </select>
                {hasFilters && <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>}
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver los cruces proyecto.' : (error.message || 'No se pudieron cargar los cruces')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando cruces…</div>
            ) : !items.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">{hasFilters ? 'Ningún cruce coincide con el filtro.' : 'Aún no hay cruces proyecto.'}</p>
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table cat-table--compact">
                        <thead>
                            <tr><th>Código</th><th>Nombre</th><th>Donadora</th><th>Toro</th><th>Cupo</th><th>Estado</th><th className="cat-table__actions-col" aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="cat-table__row">
                                    <td><span className="cat-table__name">{c.code || `#${c.id}`}</span></td>
                                    <td>{c.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{c.donor?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{c.sire?.name || <span className="cat-table__empty">Cualquier toro</span>}</td>
                                    <td>{c.capacity != null ? c.capacity : <span className="cat-table__empty">Ilimitado</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${c.status}`}>{STATUS_LABEL[c.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="projected_crosses" action="write">
                                            <button type="button" className="cat-table__edit" onClick={() => setForm(c)} disabled={removing} aria-label="Editar" title="Editar"><IconEdit /></button>
                                            <button type="button" className="cat-table__edit" onClick={() => del(c)} disabled={removing} aria-label="Eliminar" title="Eliminar"><IconTrash /></button>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && items.length > 0 && (
                <LoadMore
                    shown={items.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun="cruces"
                />
            )}

            {form && <ProjectedCrossForm cross={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
        </div>
    );
}
